import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui';
import { getMilestoneStatusColor, formatDate } from '../../../../utils/formatters/statusFormatters';
import { Milestone, JiraIssue } from '../hooks/useProjects';

interface Props {
  milestones: Milestone[];
  onAddMilestone: () => void;
  onEditMilestone: (milestone: Milestone) => void;
  onDeleteMilestone: (milestone: Milestone) => void;
}

export const MilestonesSection: React.FC<Props> = ({ milestones, onAddMilestone, onEditMilestone, onDeleteMilestone }) => {
  const [expandedMilestones, setExpandedMilestones] = useState<Record<string, boolean>>({});

  const formatStatusText = (status: string): string => {
    if (!status) return '';
    return status
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const toggleMilestone = (milestoneId: string) => {
    setExpandedMilestones(prev => ({
      ...prev,
      [milestoneId]: !prev[milestoneId]
    }));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Project Milestones</CardTitle>
          <button
            onClick={onAddMilestone}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            + Add Milestone
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {!milestones || milestones.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            No milestones yet. Add your first milestone to track project progress.
          </p>
        ) : (
          <div className="space-y-4">
            {milestones.map((milestone) => (
              <div
                key={milestone.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {milestone.name}
                      </h4>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getMilestoneStatusColor(milestone.status)}`}>
                        {formatStatusText(milestone.status)}
                      </span>
                    </div>
                    {milestone.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {milestone.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span>
                        Start: {formatDate(milestone.startDate ?? milestone.start_date)}
                      </span>
                      <span>
                        End: {formatDate(milestone.endDate ?? milestone.end_date)}
                      </span>
                      {(milestone.jira_issues?.length ?? 0) > 0 && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          {milestone.jira_issues!.length} {milestone.jira_issues!.length === 1 ? 'issue' : 'issues'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    {(milestone.jira_issues?.length ?? 0) > 0 && (
                      <button
                        onClick={() => toggleMilestone(milestone.id)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        title={expandedMilestones[milestone.id] ? 'Hide issues' : 'Show issues'}
                      >
                        <svg
                          className={`w-4 h-4 transition-transform ${expandedMilestones[milestone.id] ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => onEditMilestone(milestone)}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      title="Edit milestone"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => onDeleteMilestone(milestone)}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title="Delete milestone"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {expandedMilestones[milestone.id] && (milestone.jira_issues?.length ?? 0) > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Associated Jira Issues
                    </h5>
                    <div className="space-y-2">
                      {milestone.jira_issues!.map((issue: JiraIssue) => (
                        <div
                          key={issue.key}
                          className="flex items-start gap-3 p-2 rounded bg-gray-50 dark:bg-gray-800/50 text-sm"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <a
                                href={issue.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                {issue.key}
                              </a>
                              <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                {issue.type}
                              </span>
                              <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                {issue.status}
                              </span>
                              {issue.priority && issue.priority !== 'None' && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                                  {issue.priority}
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 dark:text-gray-400">
                              {issue.summary}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
